import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Send,
  ShoppingBag,
  Sparkles,
  MessageSquareText,
  ChevronRight,
  Bot,
  Package,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const currency = (n, code = "AUD") =>
  new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
  }).format(Number(n));

function Header({ botInfo }) {
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#05050A]/70 border-b border-white/[0.08]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-[#2AABEE] flex items-center justify-center shadow-[0_0_20px_rgba(42,171,238,0.5)]">
            <Send className="h-4 w-4 text-[#05050A]" strokeWidth={2.5} />
          </div>
          <span className="font-display text-lg tracking-tight">
            {botInfo?.shop_name || "Shop"}
            <span className="text-[#2AABEE]">.bot</span>
          </span>
        </div>
        <div
          data-testid="bot-status-indicator"
          className="flex items-center gap-2"
        >
          <span
            className={`h-2 w-2 rounded-full pulse-dot ${
              botInfo?.online ? "bg-emerald-400" : "bg-zinc-500"
            }`}
          />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
            {botInfo?.online ? "Bot online" : "Offline"}
          </span>
        </div>
      </div>
    </header>
  );
}

function ChatMock({ shopName, currencyCode }) {
  const messages = [
    { from: "user", text: "/start" },
    {
      from: "bot",
      text: `🧬 Welcome to ${shopName || "Shop"}\n\nTap a menu option below.`,
    },
    { from: "user", text: "🛍️ Browse Products" },
    {
      from: "bot",
      text: `Metabolic Research (7)\nRecovery & Regeneration (4)\nResearch Nasals (6)\n…and 10 more categories.`,
    },
    { from: "user", text: "Add BPC-157" },
    {
      from: "bot",
      text: `✅ Added.\n\n🛒 Total: ${currency(140, currencyCode)}`,
    },
  ];

  return (
    <div className="relative">
      <div className="glow-orb bg-[#2AABEE] -top-16 -right-16 h-72 w-72" />
      <div className="relative rounded-3xl border border-white/[0.08] bg-[#0F0F13] p-6 shadow-[0_0_60px_rgba(42,171,238,0.12)]">
        <div className="flex items-center gap-3 pb-4 border-b border-white/[0.06]">
          <div className="h-9 w-9 rounded-full bg-[#2AABEE]/20 flex items-center justify-center">
            <Bot className="h-4 w-4 text-[#2AABEE]" />
          </div>
          <div>
            <div className="font-semibold text-sm">
              {shopName || "Shop"} Bot
            </div>
            <div className="font-mono text-[10px] tracking-widest text-emerald-400/80 uppercase">
              ● Online
            </div>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${
                m.from === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`bubble-in max-w-[75%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-line ${
                  m.from === "user"
                    ? "bg-[#2AABEE] text-[#05050A] rounded-br-sm"
                    : "bg-white/[0.06] text-white rounded-bl-sm border border-white/[0.06]"
                }`}
                style={{ animationDelay: `${i * 180}ms` }}
              >
                {m.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Hero({ botInfo }) {
  const link =
    botInfo?.bot_link ||
    (botInfo?.bot_username ? `https://t.me/${botInfo.bot_username}` : "#");
  return (
    <section className="relative overflow-hidden bg-grid">
      <div className="glow-orb bg-[#2AABEE] -left-24 top-40 h-80 w-80" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24 md:pt-28 md:pb-32 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7">
          <motion.div
            {...fadeUp}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.03] text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 mb-8"
          >
            <Sparkles className="h-3 w-3 text-[#2AABEE]" />
            Powered by Telegram
          </motion.div>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white"
          >
            Sell anything.
            <br />
            Right inside{" "}
            <span className="text-[#2AABEE]">Telegram.</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.12 }}
            className="mt-6 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed"
          >
            A conversational storefront your customers open like any other
            chat. Browse the catalog, add to cart, see the total in{" "}
            {botInfo?.currency || "AUD"} — no app install, no checkout page.
          </motion.p>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              data-testid="open-telegram-cta"
              href={link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#2AABEE] text-[#05050A] font-bold rounded-full px-7 py-4 hover:bg-[#229ED9] transition-colors shadow-[0_0_25px_rgba(42,171,238,0.35)] hover:shadow-[0_0_40px_rgba(42,171,238,0.55)]"
            >
              <Send className="h-4 w-4" strokeWidth={2.5} />
              Open in Telegram
              <ChevronRight className="h-4 w-4" />
            </a>
            <a
              data-testid="view-catalog-cta"
              href="#catalog"
              className="inline-flex items-center gap-2 bg-white/[0.04] text-white border border-white/[0.1] rounded-full px-7 py-4 hover:bg-white/[0.08] transition-colors"
            >
              <ShoppingBag className="h-4 w-4" />
              Browse catalog
            </a>
          </motion.div>
          {botInfo?.bot_username && (
            <div className="mt-6 font-mono text-xs tracking-widest text-zinc-500 uppercase">
              @ {botInfo.bot_username}
            </div>
          )}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
          className="lg:col-span-5"
        >
          <ChatMock
            shopName={botInfo?.shop_name}
            currencyCode={botInfo?.currency || "AUD"}
          />
        </motion.div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: <Send className="h-5 w-5 text-[#2AABEE]" />,
      title: "Open the bot",
      desc: "Tap the Telegram link. No install, no login form, no account creation.",
    },
    {
      icon: <ShoppingBag className="h-5 w-5 text-[#2AABEE]" />,
      title: "Browse & add",
      desc: "Send /shop, tap products, add what you love to your cart.",
    },
    {
      icon: <MessageSquareText className="h-5 w-5 text-[#2AABEE]" />,
      title: "Checkout by chat",
      desc: "Send /checkout, review the total and message us to complete payment.",
    },
  ];

  return (
    <section className="border-y border-white/[0.08] bg-[#07070C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <motion.div {...fadeUp} className="mb-16 max-w-2xl">
          <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
            How it works
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">
            Three steps from{" "}
            <span className="text-[#2AABEE]">/start</span> to sold.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.08] rounded-2xl overflow-hidden">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="bg-[#0F0F13] p-8 relative group hover:bg-[#131319] transition-colors"
              data-testid={`step-card-${i}`}
            >
              <div className="font-mono text-xs text-zinc-600 mb-6">
                0{i + 1}
              </div>
              <div className="h-10 w-10 rounded-lg bg-[#2AABEE]/10 border border-[#2AABEE]/20 flex items-center justify-center mb-5">
                {s.icon}
              </div>
              <h3 className="text-xl font-bold tracking-tight mb-2">
                {s.title}
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Catalog({ products, currencyCode }) {
  return (
    <section id="catalog" className="relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <motion.div
          {...fadeUp}
          className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div className="max-w-2xl">
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
              Catalog · Live from the bot
            </div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              What's in the shop
              <span className="text-[#2AABEE]">.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Package className="h-4 w-4" />
            <span data-testid="product-count">
              {products.length} product{products.length === 1 ? "" : "s"}
            </span>
          </div>
        </motion.div>
        <div
          data-testid="product-grid"
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {products.map((p, i) => (
            <motion.article
              key={p.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: (i % 3) * 0.05 }}
              whileHover={{ y: -4 }}
              className="bg-[#0F0F13] border border-white/[0.08] rounded-2xl overflow-hidden hover:border-[#2AABEE]/40 transition-colors group"
              data-testid={`product-card-${p.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#05050A] flex items-center justify-center">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-5">
                <div className="font-mono text-[10px] tracking-widest uppercase text-[#2AABEE] mb-2">
                  {p.category}
                </div>
                <h3 className="text-lg font-bold tracking-tight mb-2 leading-tight">
                  {p.name}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-5 line-clamp-2">
                  {p.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-display text-xl">
                    {currency(p.price, currencyCode)}
                  </span>
                  <span className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">
                    {p.stock} in stock
                  </span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Commands({ commands }) {
  return (
    <section className="border-t border-white/[0.08] bg-[#07070C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid lg:grid-cols-12 gap-12">
        <motion.div {...fadeUp} className="lg:col-span-5">
          <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
            Bot commands
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-5">
            The whole shop in a{" "}
            <span className="text-[#2AABEE]">handful</span> of commands.
          </h2>
          <p className="text-zinc-400 leading-relaxed">
            Every action your customers need lives one keystroke away. The bot
            remembers each user's cart between sessions.
          </p>
        </motion.div>
        <motion.div {...fadeUp} className="lg:col-span-7">
          <div
            className="rounded-2xl border border-white/[0.08] bg-[#05050A] overflow-hidden shadow-[0_0_50px_rgba(42,171,238,0.08)]"
            data-testid="commands-terminal"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] bg-white/[0.02]">
              <div className="flex gap-1.5">
                <span className="h-3 w-3 rounded-full bg-red-500/70" />
                <span className="h-3 w-3 rounded-full bg-yellow-500/70" />
                <span className="h-3 w-3 rounded-full bg-emerald-500/70" />
              </div>
              <span className="ml-3 font-mono text-xs text-zinc-500">
                bot://commands
              </span>
            </div>
            <div className="p-6 font-mono text-sm space-y-3">
              {commands.map((c) => (
                <div
                  key={c.cmd}
                  className="flex items-baseline gap-4"
                  data-testid={`cmd-${c.cmd.replace("/", "")}`}
                >
                  <span className="text-[#2AABEE] font-semibold w-24 shrink-0">
                    {c.cmd}
                  </span>
                  <span className="text-zinc-400">{c.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function CTA({ botInfo }) {
  const link =
    botInfo?.bot_link ||
    (botInfo?.bot_username ? `https://t.me/${botInfo.bot_username}` : "#");
  return (
    <section className="relative overflow-hidden">
      <div className="glow-orb bg-[#2AABEE] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 h-96 w-96" />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-28 text-center relative">
        <motion.h2
          {...fadeUp}
          className="font-display text-4xl md:text-5xl leading-[0.95] mb-6"
        >
          Ready to shop?
          <br />
          <span className="text-[#2AABEE]">Chat with the bot.</span>
        </motion.h2>
        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.08 }}
          className="text-zinc-400 max-w-xl mx-auto mb-10"
        >
          Send /start and the bot will walk you through the rest.
        </motion.p>
        <motion.a
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          data-testid="footer-cta-button"
          href={link}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 bg-[#2AABEE] text-[#05050A] font-bold rounded-full px-8 py-4 hover:bg-[#229ED9] transition-colors shadow-[0_0_30px_rgba(42,171,238,0.45)]"
        >
          <Send className="h-4 w-4" strokeWidth={2.5} />
          Open in Telegram
        </motion.a>
      </div>
    </section>
  );
}

function Footer({ botInfo }) {
  return (
    <footer className="border-t border-white/[0.08]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
        <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
          © {new Date().getFullYear()} {botInfo?.shop_name || "Shop"} · Built
          with Telegram
        </span>
        <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
          {botInfo?.currency || "AUD"} · New Zealand
        </span>
      </div>
    </footer>
  );
}

export default function Landing() {
  const [botInfo, setBotInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [infoRes, prodRes] = await Promise.all([
          axios.get(`${API}/bot/info`),
          axios.get(`${API}/products`),
        ]);
        if (cancelled) return;
        setBotInfo(infoRes.data);
        setProducts(prodRes.data);
      } catch (err) {
        console.error("Failed to fetch bot data", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#05050A] text-white">
      <Header botInfo={botInfo} />
      <Hero botInfo={botInfo} />
      <HowItWorks />
      {loading ? (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            Loading catalog…
          </div>
        </div>
      ) : (
        <Catalog products={products} currencyCode={botInfo?.currency || "AUD"} />
      )}
      <Commands commands={botInfo?.commands || []} />
      <CTA botInfo={botInfo} />
      <Footer botInfo={botInfo} />
    </div>
  );
}
